import { OPTIONAL_CONTACT_FIELD_OPTIONS } from '@common/constants/contactFields';
import {
    getBuilderOptionalContactFieldLabel,
    getContactFieldLabel,
    getContactInfoTranslations,
} from '@common/i18n/contactInfo';
import type {
    ContactBlockHiddenFields,
    OptionalCoreContactFields,
} from '@common/types/templateFormFields';
import type {
    ContactFieldLabels,
    HiddenContactFields,
    RequiredContactFields,
    TemplateFormLanguage,
} from '@common/types/templateForms';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import {
    Button,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import RenameContactFieldLabelsDialog from './RenameContactFieldLabelsDialog';

const CORE_FIELDS: (keyof OptionalCoreContactFields)[] = ['email', 'cellPhone'];

interface Props {
    language: TemplateFormLanguage;
    hiddenFields: ContactBlockHiddenFields;
    requiredFields: RequiredContactFields;
    fieldLabels?: ContactFieldLabels;
    /** When provided, email and cell phone get a Required toggle (they are required by default). */
    optionalCoreFields?: OptionalCoreContactFields;
    onHiddenFieldChange: (field: keyof HiddenContactFields, checked: boolean) => void;
    onRequiredFieldChange: (field: keyof RequiredContactFields, checked: boolean) => void;
    /** When provided, a "Rename field labels" button lets the builder override field labels. */
    onFieldLabelsChange?: (fieldLabels: ContactFieldLabels | undefined) => void | Promise<void>;
    onOptionalCoreFieldChange?: (field: keyof OptionalCoreContactFields, optional: boolean) => void;
    /** When provided, email and cell phone can be hidden once they are not required. */
    onHiddenCoreFieldChange?: (field: keyof OptionalCoreContactFields, hidden: boolean) => void;
}

export default function ContactFieldsEditor({
    language,
    hiddenFields,
    requiredFields,
    fieldLabels,
    optionalCoreFields,
    onHiddenFieldChange,
    onRequiredFieldChange,
    onFieldLabelsChange,
    onOptionalCoreFieldChange,
    onHiddenCoreFieldChange,
}: Props) {
    const t = getContactInfoTranslations(language);
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const customLabelCount = Object.values(fieldLabels ?? {}).filter((label) =>
        label?.trim(),
    ).length;
    const showCoreFields = !!optionalCoreFields && !!onOptionalCoreFieldChange;

    return (
        <>
            {onFieldLabelsChange && (
                <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<DriveFileRenameOutlineIcon />}
                    onClick={() => setRenameDialogOpen(true)}
                    sx={{ mb: 2 }}
                >
                    {customLabelCount > 0
                        ? `Rename field labels (${customLabelCount})`
                        : 'Rename field labels'}
                </Button>
            )}
            <Typography variant="h4" sx={{ mb: 1 }}>
                Edit contact fields
            </Typography>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Field</TableCell>
                            <TableCell align="center" width={72}>
                                Hidden
                            </TableCell>
                            <TableCell align="center" width={72}>
                                Required
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {showCoreFields &&
                            CORE_FIELDS.map((field) => (
                                <TableRow key={field}>
                                    <TableCell>
                                        {getContactFieldLabel(field, t, fieldLabels)}
                                    </TableCell>
                                    <TableCell align="center" padding="checkbox">
                                        <Checkbox
                                            checked={!!hiddenFields[field]}
                                            disabled={
                                                !onHiddenCoreFieldChange ||
                                                !optionalCoreFields[field]
                                            }
                                            onChange={(_, checked) =>
                                                onHiddenCoreFieldChange?.(field, checked)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell align="center" padding="checkbox">
                                        <Checkbox
                                            checked={!optionalCoreFields[field]}
                                            disabled={!!hiddenFields[field]}
                                            onChange={(_, checked) =>
                                                onOptionalCoreFieldChange(field, !checked)
                                            }
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        {OPTIONAL_CONTACT_FIELD_OPTIONS.map((field) => (
                            <TableRow key={field}>
                                <TableCell>
                                    {getBuilderOptionalContactFieldLabel(field, t, fieldLabels)}
                                </TableCell>
                                <TableCell align="center" padding="checkbox">
                                    <Checkbox
                                        checked={!!hiddenFields[field]}
                                        disabled={!!requiredFields[field]}
                                        onChange={(_, checked) =>
                                            onHiddenFieldChange(field, checked)
                                        }
                                    />
                                </TableCell>
                                <TableCell align="center" padding="checkbox">
                                    <Checkbox
                                        checked={!!requiredFields[field]}
                                        disabled={!!hiddenFields[field]}
                                        onChange={(_, checked) =>
                                            onRequiredFieldChange(field, checked)
                                        }
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {onFieldLabelsChange && (
                <RenameContactFieldLabelsDialog
                    open={renameDialogOpen}
                    language={language}
                    fieldLabels={fieldLabels}
                    onClose={() => setRenameDialogOpen(false)}
                    onSave={onFieldLabelsChange}
                />
            )}
        </>
    );
}
